import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { getPartyTrustLedger, getTrustAccounts } from 'services'

import TrustAccountEntryModal from './TrustAccountEntryModal'
import TrustLedgerGrid from './TrustLedgerGrid'
import {
  type ITrustAccount,
  type ITrustAccountLedgerResponse,
  type ITrustAccountLedgerRow,
  TRUST_ACCOUNT_PURPOSES,
} from './types'

/**
 * A lender's or vendor's trust file: the accounts they own and every dollar held for them.
 *
 * The ledger is account-scoped cash, which is what makes the running balance mean something. It
 * merges the loan-level rows that settled in their accounts with the party-level entries booked
 * for them (capital wired in, a holdback held on their behalf, a transfer between accounts). Loan
 * rows are read by their account rather than copied, so nothing is counted twice.
 */
export default function PartyTrustFile({
  partyType,
  partyId,
  backTo,
  backLabel,
}: {
  partyType: 'Lender' | 'Vendor'
  partyId: number
  backTo: string
  backLabel: string
}) {
  const navigate = useHistory()
  const [party, setParty] = useState<{ name: string } | null>(null)
  const [partyAccounts, setPartyAccounts] = useState<Array<{ id: number; name: string; purposes: string[] }>>([])
  const [allAccounts, setAllAccounts] = useState<ITrustAccount[]>([])
  const [entryRow, setEntryRow] = useState<ITrustAccountLedgerRow | null | undefined>(undefined)
  const [refreshKey, setRefreshKey] = useState(0)
  // The balance the ledger struck, so the entry modal can tell whether a withdrawal is covered.
  const [totals, setTotals] = useState<ITrustAccountLedgerResponse['totals']>([])

  useEffect(() => {
    getTrustAccounts({ orderBy: 'name', orderDir: '1' })
      .then((res: any) => setAllAccounts(res?.results || []))
      .catch(() => setAllAccounts([]))
  }, [])

  const fetchRows = (filters: Record<string, any>) =>
    getPartyTrustLedger(partyType, partyId, filters).then((res: any) => {
      if (res?.party) setParty(res.party)
      setPartyAccounts(res?.accounts || [])
      return res
    })

  return (
    <div className="TrustFile-container py-6 px-2">
      <div className="max-w-screen-2xl m-auto">
        <button
          className="text-[14px] text-shade-blue hover:underline flex items-center gap-1 mb-3"
          onClick={() => navigate.push(backTo)}
        >
          <ArrowLeftIcon className="w-4 h-4" /> {backLabel}
        </button>

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold">
            {party?.name || `${partyType} #${partyId}`}
            <span className="text-gray-500 text-lg ml-3">Trust Ledger</span>
          </h2>
          <button className="text-[14px] text-shade-blue hover:underline" onClick={() => setEntryRow(null)}>
            Add transaction
          </button>
        </div>

        <div className="mb-4 text-[13px] text-gray-500">
          {partyAccounts.length ? (
            <>
              Accounts:{' '}
              {partyAccounts
                .map(
                  (account) =>
                    `${account.name} (${(account.purposes || [])
                      .map((purpose) => TRUST_ACCOUNT_PURPOSES[purpose] || purpose)
                      .join(', ')})`,
                )
                .join('  ·  ')}
            </>
          ) : (
            <>
              This {partyType.toLowerCase()} has no trust account of their own. Anything held for them in a company
              account still shows below; add an account under Accounting to route their loans' money to it.
            </>
          )}
        </div>

        <TrustLedgerGrid
          refreshKey={refreshKey}
          fetchRows={fetchRows}
          onSelectRow={(row) => setEntryRow(row)}
          onTotals={setTotals}
          emptyText="Nothing has been held for this party yet"
        />

        {entryRow !== undefined && (
          <TrustAccountEntryModal
            accounts={allAccounts}
            row={entryRow}
            defaults={{ trustAccountId: partyAccounts[0]?.id, partyType, partyId }}
            balances={totals}
            onClose={(changed: boolean) => {
              setEntryRow(undefined)
              if (changed) setRefreshKey((key) => key + 1)
            }}
          />
        )}
      </div>
    </div>
  )
}
